import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../ui";
import type { VoiceOrbState } from "./useCentinelaVoice";

const ORB_ICONS = {
  idle: "ri-mic-line",
  listening: "ri-mic-fill",
  processing: "ri-loader-4-line animate-spin",
  speaking: "ri-volume-up-fill",
} as const satisfies Record<VoiceOrbState, string>;

const RING_DELAYS = ["0ms", "500ms", "1000ms"];

export interface VoiceOrbProps {
  state: VoiceOrbState;
  size?: number;
  transcript?: string;
  analyserNode?: AnalyserNode | null;
  onClose: () => void;
}

export default function VoiceOrb({
  state,
  size = 130,
  transcript = "",
  analyserNode,
  onClose,
}: VoiceOrbProps) {
  const { t } = useTranslation("common");
  const orbRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef(0);

  useEffect(() => {
    const orb = orbRef.current;
    if (state !== "listening" || !analyserNode || !orb) return;

    const data = new Uint8Array(analyserNode.frequencyBinCount);

    const animate = () => {
      analyserNode.getByteFrequencyData(data);
      const average = data.reduce((total, value) => total + value, 0) / data.length;
      orb.style.transform = `scale(${1 + (average / 255) * 0.25})`;
      frameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(frameRef.current);
      orb.style.transform = "scale(1)";
    };
  }, [analyserNode, state]);

  return (
    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center">
      <div className="absolute inset-0 bg-app/80 backdrop-blur-sm" />

      <div className="relative z-10 flex flex-col items-center gap-6">
        {state === "speaking" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            {RING_DELAYS.map((delay) => (
              <span
                key={delay}
                className="absolute animate-voiceRing rounded-full border-2 border-primary-400/40"
                style={{ width: size, height: size, animationDelay: delay }}
              />
            ))}
          </div>
        )}

        <div
          ref={orbRef}
          className="relative flex items-center justify-center rounded-full transition-transform duration-150"
          style={{
            width: size,
            height: size,
            background:
              "radial-gradient(circle at 50% 45%, rgb(var(--c-primary) / 0.95), rgb(var(--c-primary) / 0.7) 55%, rgb(var(--c-secondary) / 0.85))",
            boxShadow:
              "0 0 40px rgb(var(--c-primary) / 0.45), 0 0 80px rgb(var(--c-primary) / 0.25)",
          }}
        >
          <span
            className="absolute animate-orbBreathe rounded-full"
            style={{
              width: size * 0.65,
              height: size * 0.65,
              background:
                "radial-gradient(circle, rgb(var(--c-on-primary) / 0.45), transparent 70%)",
            }}
          />
          <i className={`relative z-10 text-3xl text-on-primary ${ORB_ICONS[state]}`} />
        </div>

        <span className="text-[13px] font-medium text-content-secondary">
          {t(`monitoreoIa.chat.voice.state.${state}`)}
        </span>

        {transcript && (
          <p className="max-w-[320px] text-center text-sm leading-relaxed text-content">
            {transcript}
            {state === "listening" && (
              <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-primary-500 align-middle" />
            )}
          </p>
        )}

        <Button
          variant="secondary"
          size="sm"
          onClick={onClose}
          icon={<i className="ri-close-line" />}
        >
          {t("monitoreoIa.chat.voice.backToText")}
        </Button>
      </div>
    </div>
  );
}
